import { Suspense } from 'react';

import { autoFormaliseOffers } from '@api/autoFormaliseOffers';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import IwRedirect from '@iwRouter/IwRedirect';
import { useQuery } from '@tanstack/react-query';

import { Approved } from './Approved/Approved';
import { DecisionLoading } from './DecisionLoading';
import { Declined } from './Declined';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { ApplicationPendingView } from '../../../myOffers/pendingView/applicationPendingView/ApplicationPendingView';
import { BackToDashboard } from '../../BackToDashboard';

const MINIMUM_DECLINED_DELAY_MS = 5000;

function wait(ms: number) {
    return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

async function autoFormaliseOffersWithMinimumDecisionTime({
    stateKey
}: {
    stateKey: string;
}) {
    const { result } = await autoFormaliseOffers({ stateKey });

    if (result === 'DECLINED' && getCountry() === 'DE') {
        // Without this delay, the decision can feel instantaneous — as if nothing was actually checked
        await wait(MINIMUM_DECLINED_DELAY_MS);
    }

    return { result };
}

export const Decision = ({ accountId }: { accountId: string }) => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense fallback={<DecisionLoading />}>
                <AutoFormalisationResult accountId={accountId} />
            </Suspense>
        </ErrorBoundary>
    );
};

const AutoFormalisationResult = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();
    const { data } = useQuery({
        queryFn: () => autoFormaliseOffersWithMinimumDecisionTime({ stateKey }),
        queryKey: ['autoFormaliseOffers', stateKey],
        suspense: true
    });

    if (!data) throw new Error('No data from autoFormaliseOffers');

    switch (data.result) {
        case 'FORMALISED':
            return (
                <>
                    <BackToDashboard />
                    <Approved accountId={accountId} />
                </>
            );
        case 'DECLINED':
            return (
                <div className="mx-auto max-w-xl">
                    <BackToDashboard />
                    <Declined />
                </div>
            );
        case 'DOCS_REQUIRED':
            return <IwRedirect pathname="/apply-flexi-loan/" />;
        case 'DOCS_COMPLETE':
            return (
                <div data-testid="application-pending-view">
                    <ApplicationPendingView />
                </div>
            );
        default:
            throw new Error('Unknown result from autoFormaliseOffers');
    }
};
